import React, { useState, useEffect, useCallback } from 'react';
import Select, { 
  MultiValue, 
  ActionMeta, 
  createFilter,
  components,
  OptionProps,
  MultiValueProps
} from 'react-select';
import { Alert, Badge, Button, Modal, Form } from 'react-bootstrap';
import { Tag, TagOption, tagToOption, optionToTag } from '../../../Resources/Data/Tags';
import { tagService } from '../services/tagService';
import { showSuccess, showError } from '../../../Utilities/sweetAlert';
import './TagMultiSelect.css';

interface TagMultiSelectProps {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
  allowCreate?: boolean;
  maxTags?: number;
  className?: string;
}

const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
  selectedTags,
  onTagsChange,
  placeholder = "Search and select tags...",
  isDisabled = false,
  allowCreate = true,
  maxTags,
  className = ""
}) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Fetch available tags on component mount
  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tagService.getAllTags();
      setAvailableTags(response.data.tags || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
      setError('Failed to load tags. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Convert tags to options for react-select
  const tagOptions: TagOption[] = availableTags.map(tagToOption);
  const selectedOptions: TagOption[] = selectedTags.map(tagToOption);

  // Handle tag selection change
  const handleChange = (
    newValue: MultiValue<TagOption>,
    actionMeta: ActionMeta<TagOption>
  ) => {
    if (maxTags && newValue.length > maxTags) {
      showError('Maximum Tags Exceeded', `You can select up to ${maxTags} tags only.`);
      return;
    }

    const newTags = newValue.map(optionToTag);
    onTagsChange(newTags);
  };

  // Handle creating new tag
  const handleCreateTag = async () => {
    const validation = tagService.validateTagName(newTagName);
    if (!validation.isValid) {
      showError('Invalid Tag Name', validation.error || 'Invalid tag name');
      return;
    }

    const nameExists = await tagService.checkTagNameExists(newTagName, availableTags);
    if (nameExists) {
      showError('Tag Exists', 'A tag with this name already exists.');
      return;
    }

    try {
      setCreateLoading(true);
      const response = await tagService.createTag(newTagName.trim());
      
      // Extract the created tag from the response
      const createdTag: Tag = {
        id: response.data.tag.identifiers[0].id,
        name: newTagName.trim()
      };

      // Add to available tags
      setAvailableTags(prev => [...prev, createdTag]);
      
      // Add to selected tags
      const updatedSelectedTags = [...selectedTags, createdTag];
      onTagsChange(updatedSelectedTags);

      // Reset and close modal
      setNewTagName('');
      setShowCreateModal(false);
      showSuccess('Tag Created', `Tag "${createdTag.name}" has been created and added to your selection.`);
    } catch (err) {
      console.error('Error creating tag:', err);
      showError('Creation Failed', 'Failed to create tag. Please try again.');
    } finally {
      setCreateLoading(false);
    }
  };

  // Custom option component with create button
  const CustomOption = (props: OptionProps<TagOption>) => {
    return (
      <components.Option {...props}>
        <div className="d-flex justify-content-between align-items-center">
          <span>{props.data.label}</span>
          <small className="text-muted">ID: {props.data.value}</small>
        </div>
      </components.Option>
    );
  };

  // Custom multi-value component with enhanced styling
  const CustomMultiValue = (props: MultiValueProps<TagOption>) => {
    return (
      <components.MultiValue {...props}>
        <Badge bg="primary" className="tag-badge me-1">
          {props.data.label}
        </Badge>
      </components.MultiValue>
    );
  };

  // Custom no options message
  const CustomNoOptionsMessage = (props: any) => {
    return (
      <components.NoOptionsMessage {...props}>
        <div className="text-center p-3">
          <div className="mb-2">No tags found matching your search.</div>
          {allowCreate && (
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => setShowCreateModal(true)}
            >
              + Create New Tag
            </Button>
          )}
        </div>
      </components.NoOptionsMessage>
    );
  };

  // Custom styles for react-select matching admin theme
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      border: `2px solid ${state.isFocused ? 'var(--primary-light)' : 'var(--primary-lighter)'}`,
      borderRadius: '8px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(205, 133, 63, 0.2)' : 'none',
      '&:hover': {
        borderColor: 'var(--primary-light)',
      },
      minHeight: '45px',
      fontSize: '1rem',
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--primary-lighter)',
      borderRadius: '15px',
      padding: '2px 8px',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: 'var(--primary-dark)',
      fontWeight: '500',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: 'var(--primary-medium)',
      '&:hover': {
        backgroundColor: 'var(--primary-light)',
        color: 'white',
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'var(--primary-medium)' 
        : state.isFocused 
        ? 'var(--primary-lighter)' 
        : 'white',
      color: state.isSelected ? 'white' : 'var(--primary-dark)',
      '&:active': {
        backgroundColor: 'var(--primary-light)',
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--primary-medium)',
      fontSize: '1rem',
    }),
  };

  if (error) {
    return (
      <Alert variant="danger" className="mb-3">
        {error}
        <Button 
          variant="outline-danger" 
          size="sm" 
          className="ms-2"
          onClick={fetchTags}
        >
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className={`tag-multi-select ${className}`}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label mb-0">
          <strong>Tags</strong>
          {maxTags && (
            <small className="text-muted ms-2">
              ({selectedTags.length}/{maxTags})
            </small>
          )}
        </label>
        {allowCreate && (
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => setShowCreateModal(true)}
            disabled={isDisabled}
          >
            + New Tag
          </Button>
        )}
      </div>

      <Select
        isMulti
        options={tagOptions}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled || loading}
        isLoading={loading}
        filterOption={createFilter({
          ignoreCase: true,
          ignoreAccents: true,
          matchFrom: 'any',
        })}
        components={{
          Option: CustomOption,
          MultiValue: CustomMultiValue,
          NoOptionsMessage: CustomNoOptionsMessage,
        }}
        styles={customStyles}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isClearable={false}
        className="tag-select"
        classNamePrefix="tag-select"
      />

      {/* Create Tag Modal */}
      <Modal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Tag Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter tag name..."
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                maxLength={50}
                autoFocus
              />
              <Form.Text className="text-muted">
                2-50 characters. Letters, numbers, spaces, hyphens, and underscores only.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowCreateModal(false)}
            disabled={createLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateTag}
            disabled={createLoading || !newTagName.trim()}
          >
            {createLoading ? 'Creating...' : 'Create Tag'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TagMultiSelect;